import createDebug from 'debug';

import { DebugKey } from '@swf/core/enums';
import { License } from '@swf/core/types/Account';
import type { GeoComplyPLC } from '@swf/core/types/Notification';

const geoComplyDebug = createDebug(DebugKey.GeoComply);
const envId = process.env.NODE_ENV === 'production' ? 'production' : 'staging';

const licenseToInstallID = {
  [License.USPA]: 'chiY9z83vt',
  [License.USMI]: 'LWWP2AmCC4',
  [License.USNJ]: 'DfjZEeypfN',
  [License.CAON]: '7rpHtFJyGc'
};

enum State {
  Idle,
  Connected,
  InProgress
}

interface OwnProps {
  isSiteUS: boolean;
  isSiteCAON: boolean;
  license: License;
  request(props: {
    service: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD';
    body: { response: string; errorCode: number };
  }): void;
  enqueue(props: { path: string }): void;
}

type Props = GeoComplyPLC & OwnProps;

let state = State.Idle;

export const geoComplyPLC = ({
  key,
  reason,
  id: userId,
  isSiteCAON,
  isSiteUS,
  license,
  request,
  enqueue
}: Props) => {
  import(/* webpackChunkName: "GeoComply" */ './sdk/geocomply-client.min.js').then(({ Client }) => {
    const requestGeolocationCheck = () => {
      geoComplyDebug('Geolocation request started');
      Client.setLicense(key).setGeolocationReason(reason).setUserId(userId);
      Client.requestGeolocation();
      state = State.InProgress;
    };

    if (!isSiteUS && !isSiteCAON) return;

    if (state === State.Idle) {
      state = State.InProgress;
      geoComplyDebug('Geolocation connection initiated');
      Client.connect(licenseToInstallID[license as keyof typeof licenseToInstallID], envId);

      Client.on('connect', () => {
        state = State.Connected;
        geoComplyDebug('GeoComply Client connected');
        requestGeolocationCheck();
      })
        .on('error', (errorCode: number, errorMessage: string) => {
          geoComplyDebug(
            `geoClient failed - errorCode: ${errorCode}, errorMessage: ${errorMessage}`
          );
          if (
            errorCode === Client.CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE ||
            errorCode === Client.CLNT_ERROR_LOCAL_SERVICE_UNSUP_VER
          ) {
            state = State.Idle;
            Client.disconnect();
            geoComplyDebug('PLC installation required');
            enqueue({ path: '/notifications/geo-comply' });
          } else {
            state = State.Connected;
          }
        })
        .on('geolocation', (data: string) => {
          geoComplyDebug(`GeoPacket created: ${data}`);
          request({
            service: '/api/v1-preview/account/geo-location/validate',
            method: 'POST',
            body: { response: data, errorCode: 0 }
          });
          state = State.Connected;
        });
    } else if (state === State.Connected && Client.isConnected()) {
      requestGeolocationCheck();
    }
  });
};
